import { Localized } from "@fluent/react"
import { Box, Checkbox, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material"
import { unwrapResult } from "@reduxjs/toolkit"
import { MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AppId } from "../../app/appTypes"
import { DocumentNumberBox } from "../../app/components/DocumentNumberBox"
import { useAppDispatch } from "../../app/hooks"
import { LoadingContainer } from "../../app/LoadingContainer"
import { addCacheEntry, tryGetCacheEntry } from "../documents/cacheSlice"
import { loadRiskAnalysisContent, RiskAnalysisContent, RiskAnalysisFunction, RiskAnalysisGroup } from "../documents/riskAnalysis/riskAnalysisApi"
import { RequirementDescription } from "../urs/RequirementDescription"

export interface RiskAnalysisFunctionBrowserProps {
    raId: AppId
    selectedItem: RiskAnalysisFunction | null
    setSelectedItem: (item: RiskAnalysisFunction | null) => void
}

export const RiskAnalysisFunctionBrowser = (props: RiskAnalysisFunctionBrowserProps) => {
    const { raId } = props
    const dispatch = useAppDispatch()

    const [urs, setUrs] = useState<RiskAnalysisContent | null>(null)

    useEffect(() => {
        const cached = tryGetCacheEntry(raId, 'urs')
        switch (cached.tag) {
            case 'ok':
                setUrs((cached.data as RiskAnalysisContent))
                break;
            case 'expired':
            case 'nothing':
                dispatch(loadRiskAnalysisContent(raId))
                    .then(unwrapResult)
                    .then(data => {
                        setUrs(data)
                        dispatch(addCacheEntry({
                            id: raId,
                            data,
                            type: 'urs',
                        }))
                    })
                    .catch(error => console.log('Error loading RA document:' ,error))
                break;
        }
    }, [])

    if (urs === null) {
        return <LoadingContainer />
    } else {
        return <BrowserWithData {...props} ra={urs} />
    }
}

const BrowserWithData = (props: RiskAnalysisFunctionBrowserProps & { ra: RiskAnalysisContent }) => {
    const { ra } = props
    const [contextTypeId, setContextTypeId] = useState<AppId | null>(() => {
        if (ra.groups.length > 0) {
            const someItem = ra.groups[0]
            return someItem.id
        } else {
            return null
        }
    })
    const tableRef = useRef<null | HTMLDivElement>(null)

    const handleContextChange = useCallback((id: AppId | null) => setContextTypeId(id), [])

    const contextType = ra.groups.find(x => x.id === contextTypeId)

    const mask = useMemo(() => {
        return <>
            <TableCell></TableCell>
            <TableCell>Loading data...</TableCell>
            <TableCell></TableCell>
        </>
    }, [])

    // TODO: if no context render just flat table with requirements
    // if (contextType === undefined) return <span>No type</span>

    const rows = contextType
        ? <>{contextType.functions.map(fn =>
                <FunctionRowInternal
                    {...props}
                    key={fn.id}
                    fn={fn}
                    tableRef={tableRef}
                />
            )}
            {contextType.groups.map(gr => {
                <Group group={gr} />
                {gr.functions.map(innerFn =>
                    <FunctionRowInternal
                        {...props}
                        key={innerFn.id}
                        fn={innerFn}
                        tableRef={tableRef}
                    />
                )}
            })}
        </>
        : ra.functions.map(fn =>
            <FunctionRowInternal
                {...props}
                key={fn.id}
                fn={fn}
                tableRef={tableRef}
            />)

    return <Box sx={{
        display: 'flex',
        gap: 1,
    }}>
        {contextTypeId !== null && <GroupsTabs ra={ra} groupId={contextTypeId ?? ''} setContextGroupId={handleContextChange} />}
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: 50, width: 80, maxWidth: 80 }}>
                            <Typography component="span" variant="subtitle2" color="textSecondary">
                                <Localized id="urs-item-lp">Lp</Localized>
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography component="span" variant="subtitle2" color="textSecondary">
                                <Localized id="urs-item-name">Nazwa</Localized>
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ minWidth: 80, width: 80, maxWidth: 80, justifyContent: 'flex-end' }}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows}
                </TableBody>
            </Table>
        </TableContainer>
    </Box>
}

interface FunctionRowProps extends RiskAnalysisFunctionBrowserProps {
  fn: RiskAnalysisFunction
  tableRef: MutableRefObject<null | HTMLDivElement>
}
const FunctionRow = (props: FunctionRowProps & { mask: ReactNode }) => {
  const { fn: requirement, tableRef, mask } = props
  const rowRef = useRef<null | HTMLTableRowElement>(null)
  const observer = useRef<IntersectionObserver | null>(null)

  const [shown, setShown] = useState(false)

  useEffect(() => {
    if (tableRef.current && rowRef.current) {
      observer.current = new IntersectionObserver((entries) => {
        if (entries.length === 1) {
          const element = entries[0]
          if (element.isIntersecting) {
            if (observer.current) {
              observer.current.unobserve(element.target)
              observer.current.disconnect()
            }
            setShown(true)
          }
        }
      }, {
        root: tableRef.current,
        threshold: .2,
      })
      observer.current.observe(rowRef.current)
    }
  }, [])

  const content = shown ? <Memoized {...props}></Memoized> : mask

  return <TableRow ref={rowRef} id={requirement.guid} className="target:shadow-lg target:bg-slate-100 target:shadow-inner" >
    {content}
  </TableRow>
}

const Memoized = (props: FunctionRowProps) => {
    const { fn } = props

    const row = useMemo(() => {
        return <FunctionRowInternal {...props}></FunctionRowInternal>
    }, [fn.id])

    return <>{row}</>
}

const FunctionRowInternal = (props: FunctionRowProps) => {
  const { fn, selectedItem, setSelectedItem } = props

    const handleSelectionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedItem(fn)
        } else {
            setSelectedItem(null)
        }
    };

  return <TableRow>
    <TableCell
      sx={{
        paddingRight: '2px',
        textAlign: 'left',
        backgroundColor: 'inherit',
        mminWidth: 110, width: 110, maxWidth: 110
      }}
    >
      <DocumentNumberBox value={fn.numeration} />
    </TableCell>
    <TableCell sx={{
      ['& p img']: {
        maxWidth: '600px',
      },
    }}>
      {/* {code && <Chip label={code} size='small' />} */}
      <div>
        <RequirementDescription description={fn.name} />
      </div>
    </TableCell>
    <TableCell>
            <Checkbox checked={selectedItem?.id === fn.id} onChange={handleSelectionChange} />
    </TableCell>
  </TableRow>
}

export const Group = (props: { group: RiskAnalysisGroup }) => {
    const { group } = props

    return <TableRow sx={{ backgroundColor: '#f1f1f1' }}>
        <TableCell colSpan={3}>{`${group.numeration}. ${group.name}`}</TableCell>
    </TableRow>

}
interface GroupsTabsProps {
    ra: RiskAnalysisContent
    groupId: AppId
    setContextGroupId: (typeId: AppId) => void
}
export const GroupsTabs = ({ ra, groupId, setContextGroupId }: GroupsTabsProps) => {
    const allGroups = ra.groups
    const handleGroupTabChange = useCallback((_, newReqGroupId) => {
        setContextGroupId(newReqGroupId)
    }, [])
    return <div className="overflow-y-auto">
        <Tabs
            orientation="vertical"
            variant="standard"
            value={groupId}
            onChange={handleGroupTabChange}
            sx={{
                borderRight: 1,
                borderColor: 'divider',
            }}
        >
            {allGroups.map(data => <Tab
                key={data.id}
                label={data.code || data.name}
                value={data.id}
            />)}
        </Tabs>
    </div>
}
